// ListEditorCard.tsx — Target Users / Team Members list editor. Port of
// NP5ListCard from docs/design/design_handoff_add_new_product/newproduct-v4-5.jsx.
import React from "react";
import { NPIcon, NPButton, ClearChip } from "./atoms";

export function ListEditorCard({
    icon,
    title,
    items,
    addLabel,
    placeholder,
    badge,
    onChangeItem,
    onRemoveItem,
    onAddItem,
    onClear,
    onBlur,
    readOnly = false
}: {
    icon: "users" | "team";
    title: string;
    items: string[];
    addLabel: string;
    placeholder: string;
    badge?: React.ReactNode;
    onChangeItem: (index: number, value: string) => void;
    onRemoveItem: (index: number) => void;
    onAddItem: () => void;
    onClear: () => void;
    /** Called only when focus leaves the whole card, not between its controls. */
    onBlur?: () => void;
    /** Renders rows non-editable and hides add/remove controls (KAN-1464: Team
     * editing has no write path in refresh mode yet). */
    readOnly?: boolean;
}) {
    const pendingFocusIndexRef = React.useRef<number | null>(null);
    const cardRef = React.useRef<HTMLDivElement>(null);
    const suppressNextBlurRef = React.useRef(false);

    React.useEffect(() => {
        if (!onBlur) return;

        // A click on a non-focusable element outside the card is not guaranteed
        // to provide a useful relatedTarget on blur (notably after a resumed
        // draft rehydrates dynamic list rows). Observe that boundary directly
        // so leaving the card always evaluates the complete list once.
        const handlePointerDown = (event: PointerEvent) => {
            const card = cardRef.current;
            if (!card || !(event.target instanceof Node)) return;
            if (card.contains(event.target)) {
                suppressNextBlurRef.current = false;
                return;
            }
            if (card.contains(document.activeElement)) {
                suppressNextBlurRef.current = true;
                onBlur();
            }
        };

        document.addEventListener("pointerdown", handlePointerDown, true);
        return () => document.removeEventListener("pointerdown", handlePointerDown, true);
    }, [onBlur]);

    const handleAddItem = () => {
        pendingFocusIndexRef.current = items.length;
        onAddItem();
    };

    return (
        <div
            ref={cardRef}
            onFocusCapture={() => {
                suppressNextBlurRef.current = false;
            }}
            onBlur={event => {
                const nextTarget = event.relatedTarget;
                if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) return;
                if (suppressNextBlurRef.current) {
                    suppressNextBlurRef.current = false;
                    return;
                }
                onBlur?.();
            }}
            style={{
                padding: "12px 14px",
                borderRadius: 12,
                border: "1px solid var(--divider)",
                background: "var(--bg-paper)",
                display: "flex",
                flexDirection: "column",
                flex: 1
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
                <NPIcon name={icon} size={14} color="var(--fg3)" />
                <span style={{ fontSize: 12.5, fontWeight: 600 }}>{title}</span>
                {!readOnly && <ClearChip label={title} onClick={onClear} />}
                <span style={{ flex: 1 }} />
                {badge}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 7, marginBottom: 9, flex: 1 }}>
                {items.map((m, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                            className="np-list-editor-input"
                            ref={element => {
                                if (element && pendingFocusIndexRef.current === i) {
                                    pendingFocusIndexRef.current = null;
                                    element.focus();
                                }
                            }}
                            value={m}
                            readOnly={readOnly}
                            aria-label={`${title} ${i + 1}`}
                            placeholder={placeholder}
                            onChange={e => onChangeItem(i, e.target.value)}
                            style={{
                                flex: 1,
                                minWidth: 0,
                                padding: "7px 11px",
                                borderRadius: 9,
                                background: readOnly ? "var(--bg-subtle)" : "var(--bg-paper)",
                                fontSize: 12.5,
                                color: "var(--fg1)",
                                outline: "none",
                                fontFamily: "inherit"
                            }}
                        />
                        {!readOnly && (
                            <button
                                type="button"
                                title="Remove"
                                onClick={() => onRemoveItem(i)}
                                style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 8,
                                    border: "1px solid var(--divider)",
                                    background: "transparent",
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flex: "0 0 auto"
                                }}
                            >
                                <NPIcon name="x" size={12} sw={2.2} color="var(--fg4)" />
                            </button>
                        )}
                    </div>
                ))}
            </div>
            {!readOnly && (
                <div>
                    <NPButton tone="soft" small onClick={handleAddItem}>
                        <NPIcon name="plus" size={12} sw={2.2} color="var(--np-brand)" />
                        {addLabel}
                    </NPButton>
                </div>
            )}
        </div>
    );
}
